import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';

import { Button } from '../../components/ui/Button.jsx';
import { Skeleton } from '../../components/ui/Skeleton.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { financeApi } from '../../services/financeApi.js';
import { formatCurrency, formatDate } from '../../utils/formatters.js';

// ESTV form codes ("Ziffern") for the standard Swiss rates (2024+).
const RATE_CODES = { 8.1: '302', 2.6: '312', 3.8: '342' };

const QUARTERS = [1, 2, 3, 4];

function currentPeriod() {
  const now = new Date();
  return { year: now.getFullYear(), quarter: Math.floor(now.getMonth() / 3) + 1 };
}

function yearOptions(current) {
  return Array.from({ length: 6 }, (_, i) => current - i);
}

// Reads year/quarter from the URL (shareable / bookmarkable reporting period)
// with a validated fallback to the current period.
function periodFromSearchParams(searchParams) {
  const fallback = currentPeriod();
  const year = Number.parseInt(searchParams.get('year') ?? '', 10);
  const quarter = Number.parseInt(searchParams.get('quarter') ?? '', 10);
  return {
    year: Number.isInteger(year) && year >= 2000 && year <= 2100 ? year : fallback.year,
    quarter: Number.isInteger(quarter) && quarter >= 1 && quarter <= 4 ? quarter : fallback.quarter,
  };
}

export function VatReportPage() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { year, quarter } = periodFromSearchParams(searchParams);
  const nowYear = currentPeriod().year;
  const years = yearOptions(nowYear).includes(year)
    ? yearOptions(nowYear)
    : [...yearOptions(nowYear), year].sort((a, b) => b - a);

  function setPeriod(patch) {
    setSearchParams(
      { year: String(patch.year ?? year), quarter: String(patch.quarter ?? quarter) },
      { replace: true },
    );
  }

  const report = useQuery({
    queryKey: ['finance', 'reports', 'vat', year, quarter],
    queryFn: () => financeApi.vatReport({ year, quarter }),
  });

  const data = report.data;
  // Backend nulls BOTH payable and credit when the balance is exactly 0
  // (typical empty quarter) - show Ziffer 500 with 0.00 and no tint then.
  const balance = Number(data?.totals?.balance ?? 0);
  const isPayable = balance >= 0;
  const saldoValue = isPayable
    ? (data?.totals?.payable ?? data?.totals?.balance ?? 0)
    : (data?.totals?.credit ?? Math.abs(balance));
  const saldoTint =
    balance > 0
      ? 'color-mix(in oklch, var(--color-danger) 12%, transparent)'
      : balance < 0
        ? 'color-mix(in oklch, var(--color-success) 12%, transparent)'
        : undefined;

  return (
    <section className="page">
      <header className="page-header">
        <div>
          <h1>{t('vat.title')}</h1>
          <p className="muted">{t('vat.subtitle')}</p>
          {data && (
            <p className="muted text-sm">
              {[data.vatNumber, `${formatDate(data.from)} - ${formatDate(data.to)}`, t(`vat.method.${data.method || 'EFFECTIVE'}`)]
                .filter(Boolean)
                .join(' · ')}
            </p>
          )}
        </div>
        <div className="no-print" style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'flex-end' }}>
          <label className="field">
            <span className="field-label">{t('vat.year')}</span>
            <select className="select" value={year} onChange={(e) => setPeriod({ year: Number(e.target.value) })}>
              {years.map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </label>
          <label className="field">
            <span className="field-label">{t('vat.quarter')}</span>
            <select className="select" value={quarter} onChange={(e) => setPeriod({ quarter: Number(e.target.value) })}>
              {QUARTERS.map((q) => (
                <option key={q} value={q}>Q{q}</option>
              ))}
            </select>
          </label>
          <Button variant="secondary" onClick={() => financeApi.downloadVatEch0217(year, quarter)}>
            {t('vat.ech0217')}
          </Button>
          <Button variant="primary" onClick={() => window.print()}>
            {t('invoice.print.printOrSave')}
          </Button>
        </div>
      </header>

      {report.error && <div className="alert danger">{report.error?.message || t('common.loadError')}</div>}

      <div className="table-wrap">
        {report.isLoading ? (
          <Skeleton variant="table" count={8} />
        ) : data ? (
          <>
            <table className="table">
              <thead>
                <tr>
                  <th style={{ width: 80 }}>{t('vat.col.code')}</th>
                  <th>{t('vat.col.label')}</th>
                  <th className="num">{t('vat.col.base')}</th>
                  <th className="num">{t('vat.col.tax')}</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="mono">200</td>
                  <td>{t('vat.row.total')}</td>
                  <td className="num">{formatCurrency(data.turnover?.total)}</td>
                  <td className="num"></td>
                </tr>
                {Number(data.turnover?.exempt) > 0 && (
                  <tr>
                    <td className="mono">205</td>
                    <td>{t('vat.row.exempt')}</td>
                    <td className="num">{formatCurrency(data.turnover?.exempt)}</td>
                    <td className="num"></td>
                  </tr>
                )}
                {(data.rates || []).map((entry) => (
                  <tr key={entry.rate}>
                    <td className="mono">{RATE_CODES[Number(entry.rate)] || '-'}</td>
                    <td>{t('vat.row.rate')} {Number(entry.rate).toFixed(1)} %</td>
                    <td className="num">{formatCurrency(entry.base)}</td>
                    <td className="num">{formatCurrency(entry.tax)}</td>
                  </tr>
                ))}
                {data.saldo && (
                  <tr>
                    <td className="mono">322</td>
                    <td>{t('vat.row.saldo')} {Number(data.saldo.rate1).toFixed(1)} %</td>
                    <td className="num">{formatCurrency(data.saldo.grossTurnover)}</td>
                    <td className="num">{formatCurrency(data.saldo.tax)}</td>
                  </tr>
                )}
                {Number(data.acquisitionTax?.tax) > 0 && (
                  <tr>
                    <td className="mono">381</td>
                    <td>{t('vat.row.acquisitionTax')}</td>
                    <td className="num">{formatCurrency(data.acquisitionTax.base)}</td>
                    <td className="num">{formatCurrency(data.acquisitionTax.tax)}</td>
                  </tr>
                )}
                <tr>
                  <td className="mono"><strong>399</strong></td>
                  <td><strong>{t('vat.row.outputTax')}</strong></td>
                  <td className="num"></td>
                  <td className="num"><strong>{formatCurrency(data.totals?.outputTax)}</strong></td>
                </tr>
                <tr>
                  <td className="mono">400</td>
                  <td>{t('vat.row.inputTax')}</td>
                  <td className="num"></td>
                  <td className="num">{formatCurrency(data.totals?.inputTax)}</td>
                </tr>
                <tr style={saldoTint ? { background: saldoTint } : undefined}>
                  <td className="mono"><strong>{isPayable ? '500' : '510'}</strong></td>
                  <td><strong>{isPayable ? t('vat.row.payable') : t('vat.row.credit')}</strong></td>
                  <td className="num"></td>
                  <td className="num"><strong>{formatCurrency(saldoValue)}</strong></td>
                </tr>
              </tbody>
            </table>
            <p className="muted text-sm" style={{ padding: 'var(--space-2) var(--space-3)' }}>
              {t('vat.counts', {
                sales: data.counts?.salesInvoices ?? 0,
                purchases: data.counts?.purchaseInvoices ?? 0,
                credits: data.counts?.creditNotes ?? 0,
              })}
            </p>
          </>
        ) : null}
      </div>
    </section>
  );
}
